import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import { toast } from 'sonner';
import { formatDate, formatDateTime, getDayOfWeek } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Calculator, 
  Download, 
  FileSpreadsheet,
  RefreshCw,
  Users,
  Clock,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Calendar,
  Sparkles,
  Info,
  Trash2,
  Coffee,
  Wrench,
  MapPin,
  Plus,
  X,
  Edit3,
  Brain,
  Lightbulb
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const PayrollPage = () => {
  const [payrollBatches, setPayrollBatches] = useState([]);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [payrollDetails, setPayrollDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [exporting, setExporting] = useState(false);
  
  // Allowance adjustment state
  const [addAllowanceDialog, setAddAllowanceDialog] = useState(false);
  const [selectedDayForAllowance, setSelectedDayForAllowance] = useState(null); // {employeeId, date, employeeName, dayContext}
  const [availableAllowances, setAvailableAllowances] = useState([]);
  const [selectedAllowance, setSelectedAllowance] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [customName, setCustomName] = useState('');
  const [addingAllowance, setAddingAllowance] = useState(false);
  const [removingAllowance, setRemovingAllowance] = useState(null);
  
  // AI Allowance Suggestions state
  const [aiAllowanceSuggestions, setAiAllowanceSuggestions] = useState(null);
  const [loadingAiSuggestions, setLoadingAiSuggestions] = useState(false);

  const fetchPayrollBatches = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get('/payroll');
      setPayrollBatches(response.data);
      if (response.data.length > 0 && !selectedPayroll) {
        setSelectedPayroll(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch payroll batches');
    } finally {
      setLoading(false);
    }
  }, [selectedPayroll]);

  const fetchPayrollDetails = useCallback(async (payrollId) => {
    if (!payrollId) return;
    
    setLoadingDetails(true);
    try {
      const response = await API.get(`/payroll/${payrollId}`);
      setPayrollDetails(response.data);
    } catch (error) {
      toast.error('Failed to fetch payroll details');
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  useEffect(() => {
    fetchPayrollBatches();
  }, [fetchPayrollBatches]);

  useEffect(() => {
    if (selectedPayroll) {
      fetchPayrollDetails(selectedPayroll);
    }
  }, [selectedPayroll, fetchPayrollDetails]);

  const toggleEmployee = (employeeId) => {
    setExpandedEmployees(prev => ({
      ...prev,
      [employeeId]: !prev[employeeId]
    }));
  };

  const handleDelete = async (payrollId) => {
    try {
      await API.delete(`/payroll/${payrollId}`);
      toast.success('Payroll deleted - batch can be reprocessed');
      fetchPayrollBatches();
      if (selectedPayroll === payrollId) {
        setSelectedPayroll(null);
        setPayrollDetails(null);
      }
    } catch (error) {
      toast.error('Failed to delete payroll');
    }
  };

  const handleExportExcel = async () => {
    if (!selectedPayroll) return;
    setExporting(true);
    try {
      const response = await API.get(`/payroll/${selectedPayroll}/export/xlsx`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'payroll_export.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel export downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportXeroCSV = async () => {
    if (!selectedPayroll) return;
    setExporting(true);
    try {
      const response = await API.get(`/payroll/${selectedPayroll}/export/xero-csv`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'xero_payroll.csv';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Xero CSV export downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Fetch available allowances
  const fetchAvailableAllowances = async () => {
    try {
      const response = await API.get('/payroll/available-allowances');
      setAvailableAllowances(response.data);
    } catch (error) {
      console.error('Failed to fetch allowances');
    }
  };

  // Fetch AI allowance suggestions based on context
  const fetchAiAllowanceSuggestions = async (context) => {
    if (!context) {
      setAiAllowanceSuggestions(null);
      return;
    }
    
    setLoadingAiSuggestions(true);
    try {
      const params = new URLSearchParams();
      if (context.site_id) params.append('site_id', context.site_id);
      if (context.site_name) params.append('site_name', context.site_name);
      if (context.role) params.append('role', context.role);
      if (context.equipment_type) params.append('equipment_type', context.equipment_type);
      if (context.day_type) params.append('day_type', context.day_type);
      if (context.employee_id) params.append('employee_id', context.employee_id);
      
      const response = await API.get(`/ai/allowance-suggestions?${params}`);
      setAiAllowanceSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch AI suggestions:', error);
      setAiAllowanceSuggestions(null);
    } finally {
      setLoadingAiSuggestions(false);
    }
  };

  // Open add allowance dialog with context
  const openAddAllowanceDialog = (employeeId, employeeName, date, dayContext = {}) => {
    setSelectedDayForAllowance({ employeeId, employeeName, date, dayContext });
    setSelectedAllowance(null);
    setCustomAmount('');
    setCustomName('');
    fetchAvailableAllowances();
    // Fetch AI suggestions with context
    fetchAiAllowanceSuggestions({
      employee_id: employeeId,
      site_id: dayContext.site_id,
      site_name: dayContext.site_name,
      role: dayContext.role,
      equipment_type: dayContext.equipment_type,
      day_type: dayContext.day_type
    });
    setAddAllowanceDialog(true);
  };

  // Apply AI suggested allowance
  const applyAiSuggestedAllowance = (suggestion) => {
    setSelectedAllowance({
      name: suggestion.allowance_name,
      code: suggestion.allowance_code,
      amount: suggestion.suggested_amount || 0,
      unit: 'flat'
    });
    if (suggestion.suggested_amount) {
      setCustomAmount(suggestion.suggested_amount.toString());
    }
    setCustomName('');
    toast.success(`Applied: ${suggestion.allowance_name}`);
  };

  // Helper to update local payroll state
  const updateLocalPayrollState = (employeeId, date, updateFn) => {
    setPayrollDetails(prev => {
      if (!prev) return prev;
      
      const newDetails = { ...prev };
      const empIdx = newDetails.employee_summaries?.findIndex(e => e.employee_id === employeeId);
      if (empIdx === -1 || empIdx === undefined) return prev;
      
      // Deep clone the employee summaries
      newDetails.employee_summaries = [...newDetails.employee_summaries];
      newDetails.employee_summaries[empIdx] = { ...newDetails.employee_summaries[empIdx] };
      
      const emp = newDetails.employee_summaries[empIdx];
      emp.daily_breakdown = [...emp.daily_breakdown];
      
      const dayIdx = emp.daily_breakdown.findIndex(d => d.date === date);
      if (dayIdx === -1) return prev;
      
      emp.daily_breakdown[dayIdx] = { ...emp.daily_breakdown[dayIdx] };
      const day = emp.daily_breakdown[dayIdx];
      
      // Apply the update function
      updateFn(day);
      
      // Recalculate employee totals
      let empTotalAllowances = 0;
      const allowancesSummary = {};
      
      emp.daily_breakdown.forEach(d => {
        (d.allowances || []).forEach(a => {
          empTotalAllowances += a.amount || 0;
          const name = a.name || 'Unknown';
          if (!allowancesSummary[name]) {
            allowancesSummary[name] = { name, total: 0, occurrences: 0, unit: a.unit || '' };
          }
          allowancesSummary[name].total += a.amount || 0;
          allowancesSummary[name].occurrences += 1;
        });
      });
      
      emp.total_allowances = empTotalAllowances;
      emp.allowances_summary = Object.values(allowancesSummary);
      
      // Recalculate batch total
      newDetails.total_allowances = newDetails.employee_summaries.reduce(
        (sum, e) => sum + (e.total_allowances || 0), 0
      );
      
      return newDetails;
    });
  };

  // Add allowance handler - optimistic update
  const handleAddAllowance = async () => {
    if (!selectedDayForAllowance || !selectedPayroll) return;
    
    const allowance = selectedAllowance || {};
    const amount = customAmount ? parseFloat(customAmount) : allowance.amount || 0;
    const name = customName || allowance.name || 'Manual Allowance';
    
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    const newAllowance = {
      id: `temp-${Date.now()}`, // Temporary ID
      name: name,
      amount: amount,
      unit: allowance.unit || 'flat',
      code: allowance.code || 'MANUAL',
      manual: true
    };
    
    // Optimistic update
    updateLocalPayrollState(
      selectedDayForAllowance.employeeId,
      selectedDayForAllowance.date,
      (day) => {
        day.allowances = [...(day.allowances || []), newAllowance];
      }
    );
    
    setAddAllowanceDialog(false);
    setAddingAllowance(true);
    
    try {
      const response = await API.post(
        `/payroll/${selectedPayroll}/employees/${selectedDayForAllowance.employeeId}/days/${selectedDayForAllowance.date}/allowances`,
        {
          name: name,
          amount: amount,
          unit: allowance.unit || 'flat',
          code: allowance.code || 'MANUAL'
        }
      );
      
      // Update with real ID from server
      const realAllowance = response.data.allowance;
      updateLocalPayrollState(
        selectedDayForAllowance.employeeId,
        selectedDayForAllowance.date,
        (day) => {
          const idx = day.allowances.findIndex(a => a.id === newAllowance.id);
          if (idx !== -1) {
            day.allowances[idx] = realAllowance;
          }
        }
      );
      
      toast.success('Allowance added');
    } catch (error) {
      // Rollback on error
      updateLocalPayrollState(
        selectedDayForAllowance.employeeId,
        selectedDayForAllowance.date,
        (day) => {
          day.allowances = day.allowances.filter(a => a.id !== newAllowance.id);
        }
      );
      toast.error(error.response?.data?.detail || 'Failed to add allowance');
    } finally {
      setAddingAllowance(false);
    }
  };

  // Remove allowance handler - optimistic update
  const handleRemoveAllowance = async (employeeId, date, allowanceId, allowanceName, allowanceIndex) => {
    if (!selectedPayroll) return;
    
    // Find the allowance to remove (for potential rollback)
    let removedAllowance = null;
    let removedIndex = allowanceIndex;
    
    setPayrollDetails(prev => {
      const emp = prev?.employee_summaries?.find(e => e.employee_id === employeeId);
      const day = emp?.daily_breakdown?.find(d => d.date === date);
      if (day?.allowances && allowanceIndex !== undefined) {
        removedAllowance = day.allowances[allowanceIndex];
      }
      return prev;
    });
    
    // Optimistic update - remove by index (most reliable)
    updateLocalPayrollState(employeeId, date, (day) => {
      if (day.allowances && allowanceIndex !== undefined) {
        day.allowances = day.allowances.filter((_, idx) => idx !== allowanceIndex);
      }
    });
    
    setRemovingAllowance(`${employeeId}-${date}-${allowanceIndex}`);
    
    try {
      await API.delete(
        `/payroll/${selectedPayroll}/employees/${employeeId}/days/${date}/allowances/${allowanceId || allowanceName}`
      );
      toast.success('Allowance removed');
    } catch (error) {
      // Rollback on error - insert back at same position
      if (removedAllowance) {
        updateLocalPayrollState(employeeId, date, (day) => {
          const newAllowances = [...(day.allowances || [])];
          newAllowances.splice(removedIndex, 0, removedAllowance);
          day.allowances = newAllowances;
        });
      }
      toast.error(error.response?.data?.detail || 'Failed to remove allowance');
    } finally {
      setRemovingAllowance(null);
    }
  };

  // Approve/Unapprove day handler
  const handleApproveDay = async (employeeId, date, currentlyApproved) => {
    if (!selectedPayroll) return;
    
    const newApprovedState = !currentlyApproved;
    
    // Optimistic update
    updateLocalPayrollState(employeeId, date, (day) => {
      day.approved = newApprovedState;
    });
    
    try {
      await API.post(
        `/payroll/${selectedPayroll}/employees/${employeeId}/days/${date}/approve`,
        { approved: newApprovedState }
      );
    } catch (error) {
      // Rollback
      updateLocalPayrollState(employeeId, date, (day) => {
        day.approved = currentlyApproved;
      });
      toast.error('Failed to update approval status');
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return `$${value.toFixed(2)}`;
  };

  const formatHours = (value) => {
    if (value === null || value === undefined) return '-';
    return value.toFixed(1);
  };

  const getDayTypeColor = (dayType) => {
    switch (dayType) {
      case 'saturday': return 'bg-amber-100 text-amber-700';
      case 'sunday': return 'bg-red-100 text-red-700';
      case 'public_holiday': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatDayType = (dayType) => {
    if (!dayType) return 'Weekday';
    switch (dayType) {
      case 'public_holiday': return 'Public Holiday';
      case 'saturday': return 'Saturday';
      case 'sunday': return 'Sunday';
      case 'weekday': return 'Weekday';
      default: return dayType.charAt(0).toUpperCase() + dayType.slice(1);
    }
  };

  return (
    <TooltipProvider>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600">
                <Calculator className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Payroll Results</h1>
            </div>
            <p className="text-slate-600 mt-1 ml-12">View processed payroll data with detailed breakdowns</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedPayroll || ''} onValueChange={setSelectedPayroll}>
              <SelectTrigger className="w-[300px]">
                <SelectValue placeholder="Select a processed batch" />
              </SelectTrigger>
              <SelectContent>
                {payrollBatches.map((batch) => (
                  <SelectItem key={batch.id} value={batch.id}>
                    {batch.batch_name} ({batch.total_employees} employees)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              onClick={handleExportExcel}
              disabled={!selectedPayroll || exporting}
              className="gap-2"
              data-testid="export-xlsx-btn"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Export XLSX
            </Button>
            
            <Button
              variant="outline"
              onClick={handleExportXeroCSV}
              disabled={!selectedPayroll || exporting}
              className="gap-2"
              data-testid="export-xero-csv-btn"
            >
              <Download className="w-4 h-4" />
              Xero CSV
            </Button>
            
            <Button
              variant="outline"
              onClick={fetchPayrollBatches}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : payrollBatches.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calculator className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Processed Payroll</h3>
              <p className="text-slate-500">
                Process an approved timesheet batch from the Timesheets page to see payroll results here.
              </p>
            </CardContent>
          </Card>
        ) : !payrollDetails ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Info className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Select a payroll batch to view details</p>
            </CardContent>
          </Card>
        ) : loadingDetails ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <Users className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Employees</p>
                      <p className="text-xl font-bold">{payrollDetails.total_employees}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100">
                      <Clock className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Ordinary Hours</p>
                      <p className="text-xl font-bold">{formatHours(payrollDetails.total_ordinary_hours)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-100">
                      <TrendingUp className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">OT 1.5x Hours</p>
                      <p className="text-xl font-bold">{formatHours(payrollDetails.total_ot1_hours)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-100">
                      <TrendingUp className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">OT 2.0x Hours</p>
                      <p className="text-xl font-bold">{formatHours(payrollDetails.total_ot2_hours)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-100">
                      <DollarSign className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Total Allowances</p>
                      <p className="text-xl font-bold">{formatCurrency(payrollDetails.total_allowances)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Batch Summary */}
            {payrollDetails.batch_summary && (
              <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-purple-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-purple-900">Batch Summary</p>
                      <p className="text-sm text-purple-700 mt-1">{payrollDetails.batch_summary}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Review Items */}
            {payrollDetails.review_items?.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="w-4 h-4" />
                    Items to Review ({payrollDetails.review_items.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {payrollDetails.review_items.map((item, idx) => (
                      <div key={idx} className="text-sm text-amber-700 flex items-start gap-2">
                        <span className="text-amber-500">•</span>
                        <span><strong>{item.employee}:</strong> {item.item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Employee Breakdown */}
            <Card>
              <CardHeader className="py-4 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Employee Payroll Breakdown</CardTitle>
                  <Badge variant="outline">{formatDate(payrollDetails.period_start)} to {formatDate(payrollDetails.period_end)}</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {payrollDetails.employee_summaries?.map((emp) => (
                    <Collapsible 
                      key={emp.employee_id} 
                      open={expandedEmployees[emp.employee_id]}
                      onOpenChange={() => toggleEmployee(emp.employee_id)}
                    >
                      {/* Employee Header Row */}
                      <CollapsibleTrigger asChild>
                        <div className="p-4 hover:bg-slate-50 cursor-pointer">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {expandedEmployees[emp.employee_id] ? (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                              <div>
                                <p className="font-medium text-slate-900">{emp.employee_name}</p>
                                <p className="text-xs text-slate-500">{emp.total_days_worked} days worked</p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-6">
                              <div className="text-center">
                                <p className="text-xs text-slate-500">Total</p>
                                <p className="font-medium">{formatHours(emp.total_hours)}h</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-slate-500">Ordinary</p>
                                <p className="font-medium text-green-600">{formatHours(emp.total_ordinary_hours)}h</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-slate-500">OT 1.5x</p>
                                <p className="font-medium text-amber-600">{formatHours(emp.total_ot1_hours)}h</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-slate-500">OT 2.0x</p>
                                <p className="font-medium text-red-600">{formatHours(emp.total_ot2_hours)}h</p>
                              </div>
                              <div className="text-center min-w-[80px]">
                                <p className="text-xs text-slate-500">Allowances</p>
                                <p className="font-medium text-purple-600">{formatCurrency(emp.total_allowances)}</p>
                              </div>
                              {emp.gross_pay && (
                                <div className="text-center min-w-[100px] pl-4 border-l">
                                  <p className="text-xs text-slate-500">Gross Pay</p>
                                  <p className="font-bold text-lg">{formatCurrency(emp.gross_pay)}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      {/* Expanded Content */}
                      <CollapsibleContent>
                        <div className="px-4 pb-4 bg-slate-50 border-t">
                          {/* AI Summary */}
                          {emp.ai_summary && (
                            <div className="mt-4 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                              <div className="flex items-start gap-2">
                                <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-xs font-medium text-purple-700 mb-1">AI Summary</p>
                                  <p className="text-sm text-purple-900">{emp.ai_summary}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Review Items for Employee */}
                          {emp.review_items?.length > 0 && (
                            <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                              <p className="text-xs font-medium text-amber-700 mb-1 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Things to Look At
                              </p>
                              <ul className="text-sm text-amber-800 space-y-0.5">
                                {emp.review_items.map((item, idx) => (
                                  <li key={idx}>• {item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Daily Breakdown Table */}
                          <div className="mt-4">
                            <p className="text-sm font-medium text-slate-700 mb-2">Daily Breakdown</p>
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-slate-100">
                                    <TableHead className="text-xs">Date</TableHead>
                                    <TableHead className="text-xs">Site</TableHead>
                                    <TableHead className="text-xs">Role</TableHead>
                                    <TableHead className="text-xs">Equipment</TableHead>
                                    <TableHead className="text-xs">Hours</TableHead>
                                    <TableHead className="text-xs">Break</TableHead>
                                    <TableHead className="text-xs text-green-700">Ord</TableHead>
                                    <TableHead className="text-xs text-amber-700">OT1</TableHead>
                                    <TableHead className="text-xs text-red-700">OT2</TableHead>
                                    <TableHead className="text-xs">Allowances</TableHead>
                                    <TableHead className="text-xs text-center">Status</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {emp.daily_breakdown?.map((day, idx) => (
                                    <TableRow key={idx} className={day.approved ? 'bg-green-50/50' : day.flags?.length > 0 ? 'bg-amber-50/50' : ''}>
                                      <TableCell className="py-2">
                                        <div>
                                          <p className="text-sm font-medium">{day.day_of_week?.slice(0, 3)}</p>
                                          <p className="text-xs text-slate-500">{formatDate(day.date)} ({getDayOfWeek(day.date)})</p>
                                          <Badge className={`text-[10px] mt-0.5 ${getDayTypeColor(day.day_type)}`}>
                                            {formatDayType(day.day_type)}
                                          </Badge>
                                        </div>
                                      </TableCell>
                                      <TableCell className="py-2">
                                        <div className="flex items-center gap-1 text-xs">
                                          <MapPin className="w-3 h-3 text-slate-400" />
                                          <span className="truncate max-w-[100px]">{day.site_name || '-'}</span>
                                        </div>
                                        {day.docket_number && (
                                          <p className="text-[10px] text-slate-400">#{day.docket_number}</p>
                                        )}
                                      </TableCell>
                                      <TableCell className="py-2 text-xs">{day.role || '-'}</TableCell>
                                      <TableCell className="py-2">
                                        <div className="flex items-center gap-1 text-xs">
                                          <Wrench className="w-3 h-3 text-slate-400" />
                                          <span className="truncate max-w-[100px]">{day.equipment_type || '-'}</span>
                                        </div>
                                      </TableCell>
                                      <TableCell className="py-2">
                                        <p className="text-sm font-medium">{day.start_time} - {day.end_time}</p>
                                        <p className="text-xs text-slate-500">{formatHours(day.total_hours)}h total</p>
                                      </TableCell>
                                      <TableCell className="py-2">
                                        <div className="flex items-center gap-1 text-xs">
                                          <Coffee className="w-3 h-3 text-slate-400" />
                                          {day.break_minutes}m
                                          {day.break_paid && <Badge className="text-[9px] bg-green-100 text-green-700 ml-1">Paid</Badge>}
                                        </div>
                                      </TableCell>
                                      <TableCell className="py-2 text-sm font-medium text-green-700">
                                        {formatHours(day.ordinary_hours)}
                                        {day.ordinary_rate && (
                                          <p className="text-[10px] text-slate-400">@${day.ordinary_rate}/h</p>
                                        )}
                                      </TableCell>
                                      <TableCell className="py-2 text-sm font-medium text-amber-700">
                                        {formatHours(day.overtime_1_5_hours)}
                                        {day.ot1_rate && (
                                          <p className="text-[10px] text-slate-400">@${day.ot1_rate}/h</p>
                                        )}
                                      </TableCell>
                                      <TableCell className="py-2 text-sm font-medium text-red-700">
                                        {formatHours(day.overtime_2_0_hours)}
                                        {day.ot2_rate && (
                                          <p className="text-[10px] text-slate-400">@${day.ot2_rate}/h</p>
                                        )}
                                      </TableCell>
                                      <TableCell className="py-2">
                                        <div className="space-y-1">
                                          {day.allowances?.length > 0 && (
                                            <div className="space-y-0.5">
                                              {day.allowances.map((a, i) => (
                                                <div key={i} className="flex items-center gap-1 group">
                                                  <Tooltip>
                                                    <TooltipTrigger>
                                                      <Badge 
                                                        variant="outline" 
                                                        className={`text-[10px] cursor-help ${a.manual ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700'}`}
                                                      >
                                                        {a.manual && <Edit3 className="w-2 h-2 mr-0.5" />}
                                                        {a.name}: ${a.amount}
                                                      </Badge>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                      <p>{a.source_section || (a.manual ? 'Manual adjustment' : 'EBA Rule')}</p>
                                                      {a.reason && <p className="text-xs text-slate-400">{a.reason}</p>}
                                                      {a.added_by && <p className="text-xs text-slate-400">Added by: {a.added_by}</p>}
                                                    </TooltipContent>
                                                  </Tooltip>
                                                  {!day.approved && (
                                                    <button
                                                      onClick={() => handleRemoveAllowance(emp.employee_id, day.date, a.id, a.name, i)}
                                                      disabled={removingAllowance === `${emp.employee_id}-${day.date}-${i}`}
                                                      className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-red-100 rounded"
                                                      title="Remove allowance"
                                                    >
                                                      {removingAllowance === `${emp.employee_id}-${day.date}-${i}` ? (
                                                        <RefreshCw className="w-3 h-3 text-slate-400 animate-spin" />
                                                      ) : (
                                                        <X className="w-3 h-3 text-red-500" />
                                                      )}
                                                    </button>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          {!day.approved && (
                                            <button
                                              onClick={() => openAddAllowanceDialog(emp.employee_id, emp.employee_name, day.date, {
                                                site_id: day.site_id,
                                                site_name: day.site_name,
                                                role: day.role,
                                                equipment_type: day.equipment_type,
                                                day_type: day.day_type
                                              })}
                                              className="flex items-center gap-0.5 text-[10px] text-slate-400 hover:text-purple-600 transition-colors"
                                            >
                                              <Plus className="w-3 h-3" />
                                              Add
                                            </button>
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell className="py-2 text-center">
                                        {day.approved ? (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 px-2 bg-green-100 text-green-700 hover:bg-green-200"
                                                onClick={() => handleApproveDay(emp.employee_id, day.date, true)}
                                              >
                                                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                                Approved
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>Click to undo approval</p>
                                              {day.approved_by && <p className="text-xs text-slate-400">By: {day.approved_by}</p>}
                                            </TooltipContent>
                                          </Tooltip>
                                        ) : (
                                          <div className="space-y-1">
                                            {day.flags?.length > 0 && (
                                              <div className="space-y-0.5 mb-1">
                                                {day.flags.map((flag, i) => (
                                                  <Badge key={i} variant="outline" className="text-[10px] bg-amber-100 text-amber-700 flex items-center gap-0.5">
                                                    <AlertTriangle className="w-2.5 h-2.5" />
                                                    {flag}
                                                  </Badge>
                                                ))}
                                              </div>
                                            )}
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-7 px-2 text-xs"
                                              onClick={() => handleApproveDay(emp.employee_id, day.date, false)}
                                            >
                                              Approve
                                            </Button>
                                          </div>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                          
                          {/* Rules Applied Summary */}
                          {emp.rules_applied_summary?.length > 0 && (
                            <div className="mt-4 p-3 bg-white rounded-lg border">
                              <p className="text-xs font-medium text-slate-700 mb-2">Rules Applied</p>
                              <div className="flex flex-wrap gap-2">
                                {emp.rules_applied_summary.map((rule, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {rule.source_section} - {rule.rule_name} ({rule.times_applied}x)
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Allowances Summary */}
                          {emp.allowances_summary?.length > 0 && (
                            <div className="mt-3 p-3 bg-white rounded-lg border">
                              <p className="text-xs font-medium text-slate-700 mb-2">Allowances Summary</p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {emp.allowances_summary.map((a, idx) => (
                                  <div key={idx} className="p-2 bg-purple-50 rounded">
                                    <p className="text-xs text-purple-700 font-medium">{a.name}</p>
                                    <p className="text-sm font-bold text-purple-900">${a.total.toFixed(2)}</p>
                                    <p className="text-[10px] text-purple-600">{a.occurrences}x @ {a.unit}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Delete Option */}
            <div className="flex justify-end">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-red-600 hover:text-red-700 gap-2">
                    <Trash2 className="w-4 h-4" />
                    Delete & Reprocess
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Processed Payroll?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will delete the payroll results for this batch. The batch will be reset to &quot;Approved&quot; status and can be reprocessed.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => handleDelete(selectedPayroll)} 
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </>
        )}
      </div>
      
      {/* Add Allowance Dialog */}
      <Dialog open={addAllowanceDialog} onOpenChange={setAddAllowanceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Allowance</DialogTitle>
            <DialogDescription>
              {selectedDayForAllowance && (
                <>Add allowance for <strong>{selectedDayForAllowance.employeeName}</strong> on <strong>{formatDate(selectedDayForAllowance.date)}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* AI Suggestions Panel */}
            {(aiAllowanceSuggestions || loadingAiSuggestions) && (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Brain className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-800">AI Suggestions</span>
                  {loadingAiSuggestions && (
                    <RefreshCw className="w-3 h-3 text-purple-500 animate-spin" />
                  )}
                </div>
                
                {aiAllowanceSuggestions?.ai_disabled && (
                  <p className="text-xs text-slate-600">
                    AI suggestions are disabled. Enable in <span className="font-medium">Settings → AI</span> tab.
                  </p>
                )}
                
                {aiAllowanceSuggestions?.has_suggestions === false && !aiAllowanceSuggestions?.ai_disabled && (
                  <p className="text-xs text-slate-600">
                    {aiAllowanceSuggestions?.message || 'No suggestions yet. AI learns from your allowance adjustments.'}
                  </p>
                )}
                
                {aiAllowanceSuggestions?.has_suggestions && aiAllowanceSuggestions?.suggestions?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-[10px] text-slate-500">
                      Based on {aiAllowanceSuggestions.patterns_analyzed} similar contexts:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {aiAllowanceSuggestions.suggestions.slice(0, 5).map((s, i) => (
                        <Badge 
                          key={i}
                          variant="outline"
                          className="text-[10px] px-2 py-0.5 bg-white border-purple-300 cursor-pointer hover:bg-purple-100 transition-colors"
                          onClick={() => applyAiSuggestedAllowance(s)}
                        >
                          <Lightbulb className="w-2.5 h-2.5 mr-1 text-amber-500" />
                          {s.allowance_name}
                          {s.suggested_amount > 0 && ` $${s.suggested_amount}`}
                          <span className="ml-1 text-purple-500">({s.confidence}%)</span>
                        </Badge>
                      ))}
                    </div>
                    {selectedDayForAllowance?.dayContext?.role && (
                      <p className="text-[10px] text-slate-400 mt-1">
                        Context: {selectedDayForAllowance.dayContext.role}
                        {selectedDayForAllowance.dayContext.equipment_type && ` • ${selectedDayForAllowance.dayContext.equipment_type}`}
                        {selectedDayForAllowance.dayContext.site_name && ` • ${selectedDayForAllowance.dayContext.site_name}`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Select from available allowances */}
            <div className="space-y-2">
              <Label>Select Allowance Type</Label>
              <Select 
                value={selectedAllowance?.code || ''} 
                onValueChange={(code) => {
                  const allowance = availableAllowances.find(a => a.code === code);
                  setSelectedAllowance(allowance);
                  if (allowance) {
                    setCustomAmount(allowance.amount?.toString() || '');
                    setCustomName(allowance.name || '');
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose an allowance..." />
                </SelectTrigger>
                <SelectContent>
                  {availableAllowances.map((a) => (
                    <SelectItem key={a.code} value={a.code}>
                      {a.name} {a.amount > 0 && `($${a.amount})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Custom name */}
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Allowance name"
              />
            </div>
            
            {/* Custom amount */}
            <div className="space-y-2">
              <Label>Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddAllowanceDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddAllowance} 
              disabled={addingAllowance || !customAmount || parseFloat(customAmount) <= 0}
            >
              {addingAllowance ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Allowance
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default PayrollPage;
